import React from 'react';
import { Trans, useTranslation } from 'react-i18next';
import { TouchableOpacity } from 'react-native';

import { NativeStackScreenProps } from '@react-navigation/native-stack';

import { Button } from '@/components/ButtonComponent';
import { PaymentErrorModalContent } from '@/components/PaymentErrorModalContent';
import { Screen } from '@/components/ScreenComponent';
import { Text } from '@/components/TextComponent';
import { Box } from '@/components/lib/components/Box';
import { ds } from '@/components/lib/design.system';
import { useLaunchIntercomMessenger } from '@/data/intercom/intercom';
import { PaymentMethodTypes } from '@/feature/deposit/const';
import { useHasSocialWallet } from '@/hooks/use-has-social-wallet';
import { usePreventAndroidBackNavigation } from '@/hooks/use-prevent-android-back-navigation';
import { useRootNavigation } from '@/hooks/use-root-navigation';
import { useSocialWalletTranslation } from '@/hooks/use-social-wallet-translation';
import { popToHome } from '@/navigation/navigation';
import { WithdrawStackParamList } from '@/navigation/types';

import { TransactionItemResult } from '../components/TransactionItemResult';
import { useTrustlyWithdrawalSalvage } from '../hooks/use-trustly-withdrawal-salvage';
import { useGoToWithdrawHome } from '../hooks/use-withdraw-navigation';

type NavigationFailedMultipleWithdrawalProps = NativeStackScreenProps<
    WithdrawStackParamList,
    'FailedMultipleWithdrawal'
>;

export const FailedMultipleWithdrawal = ({ route }: NavigationFailedMultipleWithdrawalProps) => {
    const { t } = useTranslation(['wallet', 'common']);
    const tSocialWallet = useSocialWalletTranslation('wallet');
    const rootNavigation = useRootNavigation();
    const hasSocialWallet = useHasSocialWallet();

    usePreventAndroidBackNavigation();
    const { launchIntercomMessenger } = useLaunchIntercomMessenger();

    const goToWithdrawHome = useGoToWithdrawHome();

    const { distribution, promisesResult } = route?.params;

    const attemptedOnlyNonTrustly = (distribution ?? []).every(m => m.type !== PaymentMethodTypes.Trustly);
    const salvageActions = useTrustlyWithdrawalSalvage({ attemptedOnlyNonTrustly });

    const processedItemsList = () =>
        distribution?.map((method, idx) => ({ ...method, failed: !!promisesResult[idx]?.errors }));

    const everythingFailed = true;

    const descriptionComponent = everythingFailed ? (
        <Text textAlign="center" color={ds.colorToken.text.secondary} fontWeight="500">
            <Trans
                i18nKey={
                    hasSocialWallet
                        ? 'failed_all_withdrawals_modal_description_social'
                        : 'failed_all_withdrawals_modal_description'
                }
                ns="wallet"
            >
                We're sorry, your withdrawal did not go through. Please try again. If the problem persists contact our
                <TouchableOpacity onPress={launchIntercomMessenger}>
                    <Text fontWeight="bold" textDecorationLine="underline">
                        support team.
                    </Text>
                </TouchableOpacity>
            </Trans>
        </Text>
    ) : (
        <>
            <Text textAlign="center" color={ds.colorToken.text.secondary} fontWeight="500">
                {tSocialWallet('failed_multiple_withdrawals_modal_description')}
            </Text>
            <Box mt="s20">
                {processedItemsList()?.map(item => {
                    return <TransactionItemResult {...item} key={`method-${item.payment_type_id}`} />;
                })}
            </Box>
        </>
    );

    const secondaryButtonComponent = salvageActions.isApplicable ? (
        <Button label={t('common:return_to_withdrawals')} onPress={goToWithdrawHome} />
    ) : (
        <Button label={t('common:return_to_lobby')} onPress={() => popToHome(rootNavigation)} />
    );

    return (
        <Screen edges={['top', 'bottom']}>
            <PaymentErrorModalContent
                transactionType="withdrawal"
                onClose={() => popToHome(rootNavigation, 'account')}
                description={descriptionComponent}
                primaryButton={salvageActions.isApplicable ? salvageActions.label : t('common:retry')}
                handlePrimaryButtonPress={salvageActions.isApplicable ? salvageActions.onPress : goToWithdrawHome}
                showSecondaryButton
                secondaryButton={secondaryButtonComponent}
                invokingScreen={route.name}
                paymentMethod={distribution?.[0]?.type}
            />
        </Screen>
    );
};
